/**
 * Parsers de archivos bancarios para la conciliación (ADR 022).
 *
 * - Interbanco · "EstadoDeCuenta_<cuenta>_<yyyy>_<mm>.xls": en realidad es una tabla HTML
 *   (formato tipo SAP) con Secuencial, Fecha Movimiento, Descripción, No. Doc, Valor,
 *   Signo (+/−), Saldo Contable y Adenda ("A BANCO X, NOMBRE" / "DE BANCO X, NOMBRE").
 * - BAC · "Consulta de Transferencias.csv": encabezado de filtros + tabla
 *   Estado, Fecha (dd/mm/aaaa), Referencia, Cuenta Origen, Cuenta Destino, Monto, Moneda.
 *
 * Ambos se normalizan a MovBanco y se mandan a la RPC importar_movimientos_bancarios.
 */
export type MovBanco = {
  fecha_hora: string // ISO con offset de Guatemala
  tipo: 'CREDITO' | 'DEBITO'
  monto: number
  descripcion: string
  doc: string | null
  referencia: string | null
  contraparte_nombre: string | null
  contraparte_cuenta: string | null
  contraparte_banco: string | null
  saldo_despues: number | null
  secuencial: number | null
  raw: Record<string, string>
}

export type ParseResultado = { banco: 'INTERBANCO' | 'BAC'; cuenta: string | null; filas: MovBanco[] }

const num = (s: string | undefined | null) => {
  const n = Number(String(s ?? '').replace(/[,\sQ]/g, ''))
  return Number.isFinite(n) ? n : NaN
}
const gtIso = (fecha: string, hora = '00:00:00') => `${fecha}T${hora}-06:00`

/** "A BANCO INDUSTRIAL, JUAN PEREZ" → { banco: 'BANCO INDUSTRIAL', nombre: 'JUAN PEREZ' } */
function partirAdenda(adenda: string) {
  const a = adenda.replace(/^'+|'+$/g, '').trim()
  if (!a) return { banco: null, nombre: null }
  const sinPrefijo = a.replace(/^(A|DE)\s+/i, '')
  const coma = sinPrefijo.indexOf(',')
  if (coma === -1) return { banco: null, nombre: sinPrefijo.trim() || null }
  return { banco: sinPrefijo.slice(0, coma).trim() || null, nombre: sinPrefijo.slice(coma + 1).trim() || null }
}

export function parseInterbancoHtml(texto: string, filename = ''): ParseResultado {
  const doc = new DOMParser().parseFromString(texto, 'text/html')
  const filas = Array.from(doc.querySelectorAll('tr'))
  if (filas.length < 2) throw new Error('El archivo no tiene filas de movimientos.')
  const head = Array.from(filas[0].querySelectorAll('th,td')).map((c) => (c.textContent ?? '').trim())
  const idx = (n: string) => head.findIndex((h) => h.toLowerCase() === n.toLowerCase())
  const need = ['Fecha Movimiento', 'Descripcion Movimiento', 'No. Doc', 'Valor', 'Signo Movimiento']
  for (const n of need) if (idx(n) === -1) throw new Error(`No es un estado de cuenta de Interbanco: falta la columna "${n}".`)
  const out: MovBanco[] = []
  let cuenta: string | null = (filename.match(/EstadoDeCuenta_(\d+)/) ?? [])[1] ?? null
  for (const tr of filas.slice(1)) {
    const c = Array.from(tr.querySelectorAll('td')).map((x) => (x.textContent ?? '').trim())
    if (c.length < head.length) continue
    const raw: Record<string, string> = {}
    head.forEach((h, i) => { raw[h] = c[i] })
    const [f, h] = (raw['Fecha Movimiento'] ?? '').split(' ')
    const monto = num(raw['Valor'])
    if (!f || !Number.isFinite(monto) || monto <= 0) continue
    const ad = partirAdenda(raw['Adenda'] ?? '')
    cuenta = cuenta ?? (raw['Cuenta Origen'] || null)
    out.push({
      fecha_hora: gtIso(f, h ?? '00:00:00'),
      tipo: raw['Signo Movimiento'] === '+' ? 'CREDITO' : 'DEBITO',
      monto,
      descripcion: raw['Descripcion Movimiento'] ?? '',
      doc: raw['No. Doc'] || null,
      referencia: raw['No. Doc'] || null,
      contraparte_nombre: ad.nombre,
      contraparte_cuenta: null,
      contraparte_banco: ad.banco,
      saldo_despues: Number.isFinite(num(raw['Saldo Contable'])) ? num(raw['Saldo Contable']) : null,
      secuencial: Number.isFinite(num(raw['Secuencial'])) ? num(raw['Secuencial']) : null,
      raw,
    })
  }
  return { banco: 'INTERBANCO', cuenta, filas: out }
}

/** CSV con comillas: separa respetando "a, b". */
function csvLinea(l: string): string[] {
  const out: string[] = []; let cur = ''; let q = false
  for (let i = 0; i < l.length; i++) {
    const ch = l[i]
    if (ch === '"') { if (q && l[i + 1] === '"') { cur += '"'; i++ } else q = !q }
    else if (ch === ',' && !q) { out.push(cur); cur = '' }
    else cur += ch
  }
  out.push(cur)
  return out.map((s) => s.trim())
}

export function parseBacTransferenciasCsv(texto: string): ParseResultado {
  const lineas = texto.split(/\r?\n/)
  let producto: string | null = null
  let hi = -1
  lineas.forEach((l, i) => {
    const c = csvLinea(l)
    if (c[0] === 'Producto') producto = c[1] || null
    if (c[0] === 'Estado' && c[1] === 'Fecha' && hi === -1) hi = i
  })
  if (hi === -1) throw new Error('No es una "Consulta de Transferencias" de BAC: no encontré la tabla (Estado, Fecha, Referencia…).')
  const head = csvLinea(lineas[hi])
  const out: MovBanco[] = []
  for (const l of lineas.slice(hi + 1)) {
    if (!l.trim()) continue
    const c = csvLinea(l)
    const raw: Record<string, string> = {}
    head.forEach((h, i) => { if (h) raw[h] = c[i] ?? '' })
    const estado = (raw['Estado'] ?? '').toUpperCase()
    if (!raw['Fecha'] || /RECHAZ|PENDIENT|ANULAD|CANCEL/.test(estado)) continue
    const [d, m, y] = raw['Fecha'].split('/')
    const monto = num(raw['Monto'])
    if (!y || !Number.isFinite(monto) || monto <= 0) continue
    const [ctaO, ...nomO] = (raw['Cuenta Origen'] ?? '').split(' - ')
    const [ctaD, ...nomD] = (raw['Cuenta Destino'] ?? '').split(' - ')
    const entra = producto ? ctaD.trim() === producto : estado.startsWith('RECIB')
    out.push({
      fecha_hora: gtIso(`${y}-${m}-${d}`),
      tipo: entra ? 'CREDITO' : 'DEBITO',
      monto,
      descripcion: entra ? `Transferencia recibida (${raw['Estado']})` : `Transferencia enviada (${raw['Estado']})`,
      doc: raw['Referencia'] || null,
      referencia: raw['Referencia'] || null,
      contraparte_nombre: (entra ? nomO : nomD).join(' - ').trim() || null,
      contraparte_cuenta: (entra ? ctaO : ctaD).trim() || null,
      contraparte_banco: 'BAC',
      saldo_despues: null,
      secuencial: null,
      raw,
    })
  }
  return { banco: 'BAC', cuenta: producto, filas: out }
}

/** Detecta el formato por contenido. */
export async function parseArchivoBancario(file: File): Promise<ParseResultado> {
  // BAC exporta el CSV en Windows-1252 (tildes/ñ salen como "�" si se lee como UTF-8).
  const buf = await file.arrayBuffer()
  let texto: string
  try { texto = new TextDecoder('utf-8', { fatal: true }).decode(buf) }
  catch { texto = new TextDecoder('windows-1252').decode(buf) }
  if (/<table/i.test(texto) && /Descripcion Movimiento/i.test(texto)) return parseInterbancoHtml(texto, file.name)
  if (/Consulta de Transferencias/i.test(texto) || /"Estado","Fecha","Referencia"/.test(texto)) return parseBacTransferenciasCsv(texto)
  throw new Error('Formato no reconocido. Subí el estado de cuenta de Interbanco (.xls) o la Consulta de Transferencias de BAC (.csv).')
}
